import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Tooltip from '@material-ui/core/Tooltip';
import { addContact } from 'actions/ringCentral';
import clsx from 'clsx';
import { useChatContext } from 'features/message-center/providers';
import { Permissions, userHasPermission } from 'services/Authorization';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import { CandidateEntity } from 'UI/constants/entityTypes';
import {
  SvgEmailFilled,
  SvgMessageFilled,
  SvgPhoneFilled,
  SvgViewFilled
} from 'UI/res/icons/filled';

import { useResumeCardStyles } from './ResumeCard.styled';
import { splitInternationalPhone } from './ResumeCard.utils';

export const ResumeCardActions = React.memo(({ candidateId, phone, email, onViewDetails }) => {
  const classes = useResumeCardStyles();
  const dispatch = useDispatch();
  const { openDrawer } = useChatContext();
  const canUseTextel = userHasPermission(Permissions.Textel.CanUseModule);

  const handlePhoneClick = useCallback(() => {
    if (!phone || !candidateId) return;
    dispatch(
      addContact({
        id: candidateId,
        role: CandidateEntity.id,
        phone,
        createdAt: new Date()
      })
    );
  }, [dispatch, phone, candidateId]);

  const handleTextClick = useCallback(() => {
    if (!canUseTextel || !phone || !candidateId) return;
    const { countryCode, nationalNumber } = splitInternationalPhone(phone);
    openDrawer({
      recipient: {
        entityId: candidateId,
        entityType: 'candidate',
        countryCode,
        phone: nationalNumber
      }
    });
  }, [canUseTextel, phone, candidateId, openDrawer]);

  const handleViewDetailsClick = useCallback(() => {
    if (!candidateId || !onViewDetails) return;
    onViewDetails(candidateId);
  }, [candidateId, onViewDetails]);

  return (
    <>
      <When condition={Boolean(candidateId && onViewDetails)}>
        <Tooltip title={strings.shared.rowActions.actions.viewDetails} placement="bottom" arrow>
          <IconButton
            aria-label={strings.shared.rowActions.actions.viewDetails}
            onClick={handleViewDetailsClick}
            className={classes.actionButton}
          >
            <SvgViewFilled className={classes.actionIcon} />
          </IconButton>
        </Tooltip>
      </When>
      <div className={clsx(!phone && classes.disabledAction)}>
        <Tooltip title={strings.shared.rowActions.actions.call} placement="bottom" arrow>
          <Link
            href={phone ? `tel:${phone}` : undefined}
            onClick={handlePhoneClick}
            className={classes.actionButton}
          >
            <SvgPhoneFilled className={classes.actionIcon} />
          </Link>
        </Tooltip>
      </div>
      <div className={clsx(!phone && classes.disabledAction)}>
        <Tooltip title={strings.shared.rowActions.actions.text} placement="bottom" arrow>
          {canUseTextel ? (
            <IconButton onClick={handleTextClick} className={classes.actionButton}>
              <SvgMessageFilled className={classes.actionIcon} />
            </IconButton>
          ) : (
            <Link
              href={phone ? `sms:${phone}` : undefined}
              onClick={handlePhoneClick}
              className={classes.actionButton}
            >
              <SvgMessageFilled className={classes.actionIcon} />
            </Link>
          )}
        </Tooltip>
      </div>
      <div className={clsx(!email && classes.disabledAction)}>
        <Tooltip title={strings.shared.rowActions.actions.email} placement="bottom" arrow>
          <Link href={email ? `mailto:${email}` : undefined} className={classes.actionButton}>
            <SvgEmailFilled className={classes.actionIcon} />
          </Link>
        </Tooltip>
      </div>
    </>
  );
});
